import { fetchData, postData, patchData, putData } from 'modules/Axios';
import { showToast } from 'modules/Notifications';
import { isNotNilOrEmpty, isFalsy } from 'lib/Helpers';
import { isSegmentNameValid } from 'lib/Segments';

import { mapApiSegmentProperties, mapUiSegmentProperties } from '../utils/index';

export const defaultState = {
  isNameUnique: true,
  loading: false,
  locations: [],
  currentLocationPage: 0,
  locationSearchValue: '',
  totalLocationRecords: Number.MAX_SAFE_INTEGER,
  dayParts: [],
  ethnicities: [],
  orderingChannels: [],
  nameValid: true,
  segmentData: {
    name: '',
    description: '',
    locations: [],
    includeWithoutLocation: false,
    filters: [],
    profileSource: 'ALL',
  },
};

export const EDIT_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS = 'EDIT_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS';

export const receiveBrandOrderingChannels = orderingChannels => {
  return {
    type: EDIT_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS,
    payload: orderingChannels,
  };
};

function uniqueBy(arr, prop) {
  return arr.reduce((a, d) => {
    if (!a.includes(d[prop])) { a.push(d[prop]); }
    return a;
  }, []);
}

export const fetchBrandOrderingChannels = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/ordering-channel`))
      .catch(e => console.log('Could not fetch brand ethnicity: ', e))) || [];

    const channelsWithOtherOptionExcluded = data.filter(c => c.name.toLowerCase() !== 'other');

    const distinctOrderingChannels = uniqueBy(channelsWithOtherOptionExcluded, 'name');
    const hasOther = distinctOrderingChannels.find(x => x.toLowerCase() === 'other');
    if (!hasOther) {
      distinctOrderingChannels.push('Other');
    }
    const result = distinctOrderingChannels.map(o => ({ text: o, value: o }));
    return dispatch(receiveBrandOrderingChannels(result));
  };
};

export const UPDATE_EDIT_SEGMENT_DATA = 'UPDATE_EDIT_SEGMENT_DATA';
/**
 * Central Change Handler for All Sections of Segment Data
 * @param {string} field
 * @param {*} payload
 * @return {Function} dispatch of UPDATE_SEGMENT_DATA Payload
 */
export const updateSegmentData = (field, payload) => {
  return (dispatch, getState) => {
    const { nameValid, segmentData } = getState().editSegment;
    const data = { ...segmentData, [field]: payload };

    return dispatch({
      type: UPDATE_EDIT_SEGMENT_DATA,
      payload: {
        segmentData: data,
        nameValid: field === 'name'
          ? isSegmentNameValid(payload)
          : nameValid,
      },
    });
  };
};

export const EDIT_LOADING = 'EDIT_LOADING';
/**
 * Toggles Global Loading State for this Section
 * @param {boolean} bool
 * @return {Object} EDIT_LOADING Payload
 */
export const editLoading = bool => {
  return {
    type: EDIT_LOADING,
    payload: bool
  };
};

export const RECEIVE_LOCATIONS_EDIT_SEGMENT = 'RECEIVE_LOCATIONS_EDIT_SEGMENT';
/**
 * Receiver to Update Locations State
 * @param {Array.Object} locations
 * @return {Object} RECEIVE_LOCATIONS Payload
 */
export const receiveLocations = locations => {
  return {
    type: RECEIVE_LOCATIONS_EDIT_SEGMENT,
    payload: locations,
  };
};

export const RECEIVE_SEGMENT = 'RECEIVE_SEGMENT';
/**
 * Receiver to Update Segment State
 * @param {Object} segment
 * @return {Object} RECEIVE_SEGMENT Payload
 */
export const receiveSegment = segment => {
  return {
    type: RECEIVE_SEGMENT,
    payload: segment,
  };
};

export const updateLocationSearchValue = (searchValue) => {
  return async (dispatch, getState) => {
    await Promise.all([
      dispatch(updateEditSegmentLocationsPage(0)),
      dispatch(updateCreateSegmentLocationSearchValue(searchValue)),
    ]).then(() => {
      dispatch(fetchLocations(true));
    });
  };
};

export const UPDATE_EDIT_SEGMENT_LOCATION_SEARCH_VALUE = 'UPDATE_EDIT_SEGMENT_LOCATION_SEARCH_VALUE';
export const updateCreateSegmentLocationSearchValue = searchValue => {
  return {
    type: UPDATE_EDIT_SEGMENT_LOCATION_SEARCH_VALUE,
    payload: searchValue,
  };
};

export const fetchLocations = (shouldUpdateGrid = false) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const { currentLocationPage, locations, locationSearchValue } = getState().editSegment;
    const url = `/brands/${brand.id}/sites?size=20&page=${currentLocationPage}&sort=city,ASC`;
    try {
      const locationsData = locationSearchValue
        ? await dispatch(postData(url, { filter: {
          name: locationSearchValue,
          store_id: locationSearchValue,
          city: locationSearchValue,
          state: locationSearchValue,
          postal_code: locationSearchValue
        } }))
        : await dispatch(fetchData(url));

      if (currentLocationPage > locationsData.total_pages) {
        return;
      }
      dispatch(updateEditSegmentLocationsPage(currentLocationPage + 1));
      dispatch(receiveLocations({
        ...locationsData,
        locations: shouldUpdateGrid ? locationsData.content : locations.concat(locationsData.content),
      }));
    } catch (e) {
      console.log('Could not fetch brand locations: ', e);
    }
  };
};

export const UPDATE_EDIT_SEGMENT_LOCATIONS_PAGE = 'UPDATE_EDIT_SEGMENT_LOCATIONS_PAGE';
export const updateEditSegmentLocationsPage = page => ({
  type: UPDATE_EDIT_SEGMENT_LOCATIONS_PAGE,
  payload: page,
});

export const RECEIVE_BRAND_DATE_PARTS = 'RECEIVE_BRAND_DATE_PARTS';
/**
 * Receiver to Update Day Parts State
 * @param {Array.Object} day parts
 * @return {Object} RECEIVE_BRAND_DATE_PARTS Payload
 */
export const receiveBrandDateParts = dayParts => {
  return {
    type: RECEIVE_BRAND_DATE_PARTS,
    payload: dayParts,
  };
};

/**
 * Fetches Brand Day Parts
 * @return {Function} dispatch(receiveBrandDateParts())
 */
export const fetchBrandDayParts = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/day-parts`))
      .catch(e => console.log('Could not fetch brand day parts: ', e))) || [];
    const dayParts = data.sort((a, b) => (a.id - b.id)).map(o => ({ text: o.name, value: o.id }));
    return dispatch(receiveBrandDateParts(dayParts));
  };
};

export const RECEIVE_BRAND_ETHNICITIES = 'RECEIVE_BRAND_ETHNICITIES';
/**
 * Receiver to Update Ethnicities State
 * @param {Array.Object} Ethnicities
 * @return {Object} RECEIVE_BRAND_ETHNICITIES Payload
 */
export const receiveBrandEthnicities = ethnicities => {
  return {
    type: RECEIVE_BRAND_ETHNICITIES,
    payload: ethnicities,
  };
};

/**
 * Fetches Brand Ethnicities
 * @return {Function} dispatch(receiveBrandEthnicities())
 */
export const fetchBrandEthnicities = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const data = (await dispatch(fetchData(`/brands/${brand.id}/brands-api/ethnicity`))
      .catch(e => console.log('Could not fetch brand ethnicity: ', e))) || [];
    const ethnicities = data.map(o => ({ text: o.name, value: o.id }));
    return dispatch(receiveBrandEthnicities(ethnicities));
  };
};

export const FETCH_EXISTING_SEGMENT = 'FETCH_EXISTING_SEGMENT';
/**
 * Fetches Existing Segment Data from API & Inits Fetching Locations
 * @param {string} id -- Segment ID from Route Params
 * @return {Function} dispatch(receiveSegment())
 */
export const fetchExistingSegment = id => {
  return async (dispatch, getState) => {
    dispatch(editLoading(true));
    const { brand } = getState().app;
    const segment = await dispatch(fetchData(`/brands/${brand.id}/segments/${id}`))
      .catch(e => {
        dispatch(showToast({
          state: 'ERROR',
          title: 'Could not fetch this segment',
          message: e.message,
          dismissible: true,
          dismissAfter: 3000,
        }));
        dispatch(editLoading(false));
        return { ...defaultState.segmentData };
      });
    return dispatch(receiveSegment(mapApiSegmentProperties(segment)));
  };
};

export const INIT_EDIT_SEGMENT = 'INIT_EDIT_SEGMENT';
/**
 * Initialize Edit Segment Flow
 * @param {Object} Filters
 * @return {Function} Dispatch fetchLocations
 */
export const initEditSegment = id => {
  return async dispatch => {
    dispatch(editLoading(true));
    await Promise.all([
      dispatch(fetchLocations()),
      dispatch(fetchBrandDayParts()),
      dispatch(fetchBrandEthnicities()),
      dispatch(fetchBrandOrderingChannels()),
    ]).catch(e => {
      console.log('Init Edit Segment Promise.all Failed...', e);
      return dispatch(editLoading(false));
    });
    return dispatch(fetchExistingSegment(id));
  };
};

export const saveSegment = (cb, redirect = true) => {
  return async (dispatch, getState) => {
    dispatch(editLoading(true));
    const { brand } = getState().app;
    const { segmentData } = getState().editSegment;
    const { id } = segmentData;
    const data = mapUiSegmentProperties(segmentData);

    const segment = await dispatch(putData(`/brands/${brand.id}/segments/${id}`, data))
      .catch(e => {});
    if (isFalsy(segment)) {
      console.log('Saving Segment Failed...');
      dispatch(showToast({
        state: 'ERROR',
        title: 'Error',
        message: 'Issue creating this segment. Please try again later',
        dismissible: true,
        dismissAfter: 1500,
      }));
      return dispatch(editLoading(false));
    }
    if (redirect) {
      dispatch(editLoading(false));
      dispatch(showToast({
        state: 'SUCCESS',
        title: 'Success!',
        message: 'Segment saved',
        dismissible: true,
        dismissAfter: 3000,
      }));
      return cb();
    }
    return null;
  };
};

export const activateSegment = cb => {
  return async (dispatch, getState) => {
    dispatch(editLoading(true));
    const { brand } = getState().app;
    const { segmentData: { id } } = getState().editSegment;

    await dispatch(saveSegment(cb, false));
    const submitted = await dispatch(patchData(`/brands/${brand.id}/segments/${id}/activate`, {}))
      .catch(e => {
        console.log('Submitting Segment Failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue submitting this segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        dispatch(editLoading(false));
        return null;
      });
    /* Submit attempt failed, return null and keep user on the page. */
    if (isFalsy(submitted)) return null;

    dispatch(editLoading(false));
    dispatch(showToast({
      state: 'SUCCESS',
      title: 'Success!',
      message: 'Segment submitted for processing',
      dismissible: true,
      dismissAfter: 3000,
    }));
    return cb();
  };
};

export const EDIT_SEGMENT_CHECK_NAME_UNIQUENESS = 'EDIT_SEGMENT_CHECK_NAME_UNIQUENESS';
/**
 * Uses `isUnique` Endpoint to Verify if Given Segment Name is Unique
 * @return {boolean}
 */
export const checkNameUniqueness = () => {
  return async (dispatch, getState) => {
    const { segmentData: { id, name } } = getState().editSegment;
    const { brand } = getState().app;

    const data = await dispatch(postData(`/brands/${brand.id}/segments/names/isUnique`, { id, name }))
      .catch(e => {
        console.log('There was an issue verifying name uniqueness...', e);
        return { isUnique: false };
      });
    return dispatch({
      type: EDIT_SEGMENT_CHECK_NAME_UNIQUENESS,
      payload: data.isUnique,
    });
  };
};

export const RESET_SEGMENT_ROUTE = 'RESET_SEGMENT_ROUTE';
export const resetSegmentRoute = () => {
  return {
    type: RESET_SEGMENT_ROUTE,
  };
};

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case EDIT_LOADING:
      return { ...state, loading: action.payload };
    case EDIT_SEGMENT_CHECK_NAME_UNIQUENESS:
      return { ...state, isNameUnique: action.payload };
    case UPDATE_EDIT_SEGMENT_LOCATIONS_PAGE:
      return { ...state, currentLocationPage: action.payload };
    case RECEIVE_LOCATIONS_EDIT_SEGMENT:
      return {
        ...state,
        locations: action.payload.locations,
        totalLocationRecords: isNotNilOrEmpty(action.payload.total_elements) ? action.payload.total_elements : state.totalRecords, };
    case UPDATE_EDIT_SEGMENT_LOCATION_SEARCH_VALUE:
      return {
        ...state,
        locationSearchValue: action.payload,
      };
    case RECEIVE_BRAND_DATE_PARTS:
      return { ...state, dayParts: action.payload };
    case RECEIVE_BRAND_ETHNICITIES:
      return { ...state, ethnicities: action.payload };
    case EDIT_SEGMENT_RECEIVE_BRAND_ORDERING_CHANNELS:
      return { ...state, orderingChannels: action.payload };
    case RECEIVE_SEGMENT:
      return { ...state, segmentData: action.payload, loading: false };
    case RESET_SEGMENT_ROUTE:
      return { ...defaultState };
    case UPDATE_EDIT_SEGMENT_DATA:
      return {
        ...state,
        nameValid: action.payload.nameValid,
        segmentData: action.payload.segmentData,
      };
    default:
      return state;
  }
}
